import React from 'react';
import { useTranslation } from 'react-i18next';

type LeadStatus = 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'DELETED';
type DealStatus =
  | 'OPEN'
  | 'QUALIFIED'
  | 'OUTREACH_STARTED'
  | 'MEETING_SCHEDULED'
  | 'PROPOSAL_SENT'
  | 'IN_NEGOTIATION'
  | 'CLOSED_WON'
  | 'CLOSED_LOST';

interface LeadProgressBarProps {
  leadStatus: LeadStatus;
  dealStatus?: DealStatus | null;
  compact?: boolean;
}

const STEP_ORDER: DealStatus[] = [
  'OPEN',
  'QUALIFIED',
  'OUTREACH_STARTED',
  'MEETING_SCHEDULED',
  'PROPOSAL_SENT',
  'IN_NEGOTIATION',
  'CLOSED_WON',
];

const LeadProgressBar: React.FC<LeadProgressBarProps> = ({ leadStatus, dealStatus, compact = false }) => {
  const { t } = useTranslation();

  const terminalStatus = leadStatus === 'REJECTED'
    ? 'REJECTED'
    : leadStatus === 'DELETED'
      ? 'DELETED'
      : dealStatus === 'CLOSED_LOST'
        ? 'CLOSED_LOST'
        : null;

  const activeStatus: DealStatus = dealStatus || (leadStatus === 'ACCEPTED' ? 'QUALIFIED' : 'OPEN');
  const activeIndex = STEP_ORDER.indexOf(activeStatus);

  if (terminalStatus) {
    return (
      <div className={`rounded-xl border px-3 py-3 ${terminalStatus === 'CLOSED_LOST' || terminalStatus === 'REJECTED' ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-slate-50'}`}>
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          {t('partnerProgress.finalStatus')}
        </div>
        <div className={`mt-1 text-sm font-semibold ${terminalStatus === 'CLOSED_LOST' || terminalStatus === 'REJECTED' ? 'text-rose-700' : 'text-slate-700'}`}>
          {terminalStatus === 'CLOSED_LOST'
            ? t('partnerProgress.outcomes.CLOSED_LOST')
            : terminalStatus === 'REJECTED'
              ? t('partnerProgress.outcomes.REJECTED')
              : t('partnerProgress.outcomes.DELETED')}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          {t('partnerProgress.title')}
        </div>
        <div className="text-xs font-medium text-slate-600">
          {t(`partnerProgress.steps.${activeStatus}`)}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-1.5">
        {STEP_ORDER.map((step, index) => {
          const isComplete = index <= activeIndex;
          const isCurrent = index === activeIndex;
          return (
            <div
              key={step}
              className={`h-2 flex-1 rounded-full transition-colors ${isComplete ? 'bg-[#47ABDB]' : 'bg-slate-200'} ${isCurrent ? 'ring-2 ring-[#47ABDB]/25' : ''}`}
              aria-hidden="true"
            />
          );
        })}
      </div>
      {!compact && (
        <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-500 sm:grid-cols-4 xl:grid-cols-7">
          {STEP_ORDER.map((step) => (
            <span key={step}>{t(`partnerProgress.steps.${step}`)}</span>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadProgressBar;
